import { useSyncExternalStore } from "react";
import { db } from "./db";
import { probe, isNetworkError } from "./net";
import { createDispatchRemote, extractCard } from "./dispatchCreate";
import { mergeExtraction } from "./cardMerge";
import { supabase } from "./supabase";
import { ensureSession, isAuthError } from "./authState";
import { upsertCrew } from "./crew";

const BACKOFF = [30_000, 120_000, 300_000];
let state = { items: [], flushing: false, nextAt: null, attempt: 0, paused: false };
const subs = new Set();
const set = (patch) => { state = { ...state, ...patch }; subs.forEach((f) => f()); };
let timer = null, ctx = null;

export const useOutbox = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => state, () => state);

export const loadOutbox = async () => { const items = (await db.all("outbox")).sort((a, b) => a.created_at.localeCompare(b.created_at)); set({ items }); return items; };

export const setOutboxContext = (c) => { ctx = c; };

export const toQueuedDispatch = (it) => ({
  id: `outbox:${it.idempotency_key}`, job_token: it.job_token, status: it.markSent ? "queued_sent" : "queued", queued: true, outbox: it,
  title: it.card?.title || it.text?.slice(0, 60) || "Queued job", task_summary: it.text || it.card?.title || "", tasks: it.card?.tasks || [], hazards: it.card?.hazards || [],
  transcript: it.card?.transcript || "", latitude: it.pin?.lat ?? null, longitude: it.pin?.lng ?? null, recipient_phone: it.contact?.phone || null, recipient_email: it.contact?.email || null, recipient_name: it.contact?.contactName || null,
  access_code: it.card?.access_code || null, access_notes: it.card?.access_notes || null, sender_name: it.senderName || null, created_at: it.created_at, properties: it.siteLabel ? { title: it.siteLabel } : null,
  sync_state: it.sync_state || "queued", sync_error: it.sync_error || null,
});

export const enqueue = async (item) => {
  const full = { ...item, created_at: new Date().toISOString(), sync_state: "queued", attempts: 0 };
  await db.put("outbox", full);
  await loadOutbox();
  try { const r = await navigator.serviceWorker?.ready; await r?.sync?.register("dropit-outbox"); } catch { /* unsupported */ }
  schedule(0);
  return full;
};

export const markQueuedSent = async (key) => { const it = await db.get("outbox", key); if (it) { await db.put("outbox", { ...it, markSent: true }); await loadOutbox(); } };
export const removeOutbox = async (key) => { await db.del("outbox", key); await loadOutbox(); };
export const retryItem = async (key) => { const it = await db.get("outbox", key); if (it) { await db.put("outbox", { ...it, sync_state: "queued", sync_error: null, attempts: 0 }); await loadOutbox(); flush(); } };

const schedule = (ms) => { clearTimeout(timer); set({ nextAt: ms ? Date.now() + ms : null }); timer = setTimeout(flush, ms); };

export const flush = async () => {
  if (state.flushing || !ctx?.user) return;
  const items = await loadOutbox();
  const pending = items.filter((i) => i.sync_state !== "failed");
  if (!pending.length) { set({ nextAt: null, attempt: 0 }); return; }
  if (!(await probe())) return schedule(BACKOFF[Math.min(state.attempt, BACKOFF.length - 1)]) || set({ attempt: state.attempt + 1 });
  const auth = await ensureSession();
  if (auth.expired) return set({ paused: true, nextAt: null });
  if (!auth.session) return schedule(BACKOFF[Math.min(state.attempt, BACKOFF.length - 1)]) || set({ attempt: state.attempt + 1 });
  set({ flushing: true, nextAt: null, paused: false });
  let transient = false;
  for (const it of pending) {
    try {
      await db.put("outbox", { ...it, sync_state: "syncing" }); await loadOutbox();
      let card = it.card;
      if (!card?.title || card.provisional) {
        const d = await extractCard({ audioBlob: it.audioBlob, photos: it.photos, text: it.text });
        card = mergeExtraction(it.card || {}, d, it.edited, { withAudio: !!it.audioBlob }).card;  // same rules as the page: never clobber user edits
        await db.put("outbox", { ...it, card });
      }
      const knownSite = it.knownSiteId ? (await db.get("properties", it.knownSiteId)) || { id: it.knownSiteId } : null;
      if (it.contact?.crewContactId && String(it.contact.crewContactId).startsWith("local:")) {
        const local = await db.get("crew", it.contact.crewContactId);
        const saved = local ? await upsertCrew(ctx.user.id, local, { allowLocal: false }).catch(() => null) : null;
        if (saved) { it.contact = { ...it.contact, crewContactId: saved.id, contactName: it.contact.contactName || saved.name }; await db.put("outbox", it); }
      }
      const dispatch = await createDispatchRemote({ user: ctx.user, senderName: it.senderName, card, contact: it.contact, pin: it.pin, siteLabel: it.siteLabel, knownSite, audioBlob: it.audioBlob, photos: it.photos, idemKey: it.idempotency_key, jobToken: it.job_token });
      if (it.markSent && dispatch.status === "draft") await supabase.from("dispatches").update({ status: "sent" }).eq("id", dispatch.id);
      await db.del("outbox", it.idempotency_key);
      ctx.onSynced?.(dispatch);
    } catch (e) {
      if (e.code === "AUTH" || isAuthError(e)) {
        transient = true;
        await db.put("outbox", { ...it, sync_state: "queued", sync_error: "session" });
        const again = await ensureSession();
        if (again.expired) { await loadOutbox(); set({ flushing: false, paused: true, nextAt: null }); return; }
      } else if (e.permanent || (e.status && e.status < 500 && e.status !== 401) || e.code === "RPC") {
        await db.put("outbox", { ...it, sync_state: "failed", sync_error: e.code || e.message });
      } else {
        transient = true;
        await db.put("outbox", { ...it, sync_state: "queued", attempts: (it.attempts || 0) + 1, sync_error: isNetworkError(e) ? "offline" : e.message });
      }
    }
  }
  await loadOutbox();
  set({ flushing: false });
  ctx.onFlushed?.();
  if (transient) { schedule(BACKOFF[Math.min(state.attempt, BACKOFF.length - 1)]); set({ attempt: state.attempt + 1 }); } else set({ attempt: 0, nextAt: null });
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => schedule(1500));
  window.addEventListener("focus", () => { if (state.items.some((i) => i.sync_state !== "failed")) schedule(500); });
  navigator.serviceWorker?.addEventListener("message", (e) => { if (e.data?.type === "FLUSH_OUTBOX") schedule(0); });
}
